import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';

import * as L from '../StudentDetail.styled';
import { IAgreement } from './types';

interface IRequisitesBlockProps {
  agreement: IAgreement;
}

/**
 * Requisites that go onto invoices: legal name, taxId/NIP, address,
 * payer mode, VAT flag, country, company. Bulk of the data flows
 * automatically from the PandaDoc contract during provisioning — see
 * backend §5 in `docs/fakturownia/backend_tz_student_card.md` (RESOLVED).
 *
 * Each field is independently nullable so we render «—» rather than hide
 * the row; reading «what's missing» is part of the financier's workflow.
 */
export const RequisitesBlock = memo(({ agreement }: IRequisitesBlockProps) => {
  const { t } = useTranslation();

  const {
    paymentByCompany: byCompany,
    vat,
    legalName,
    taxId,
    address,
    street,
    city,
    zipCode,
  } = agreement;

  const composedAddress =
    address ||
    [street, [city, zipCode].filter(Boolean).join(' ')]
      .filter(Boolean)
      .join(', ') ||
    null;

  return (
    <L.Subblock>
      <L.SubblockHeader>
        {t('pages.studentDetail.requisites.title')}
      </L.SubblockHeader>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: 8,
          fontSize: 12,
        }}
      >
        <div>
          <L.Muted>{t('pages.studentDetail.requisites.payer')}:&nbsp;</L.Muted>
          {byCompany
            ? t('pages.studentDetail.requisites.company')
            : t('pages.studentDetail.requisites.individual')}
        </div>
        <div>
          <L.Muted>
            {t('pages.studentDetail.requisites.legalName')}:&nbsp;
          </L.Muted>
          {legalName || agreement.company?.name || '—'}
        </div>
        <div>
          <L.Muted>{t('pages.studentDetail.requisites.taxId')}:&nbsp;</L.Muted>
          {taxId || '—'}
        </div>
        <div>
          <L.Muted>
            {t('pages.studentDetail.requisites.country')}:&nbsp;
          </L.Muted>
          {agreement.country?.title || '—'}
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <L.Muted>
            {t('pages.studentDetail.requisites.address')}:&nbsp;
          </L.Muted>
          {composedAddress || '—'}
        </div>
        <div>
          <L.Muted>{t('pages.studentDetail.requisites.vat')}:&nbsp;</L.Muted>
          {vat
            ? t('pages.studentDetail.requisites.vatYes')
            : t('pages.studentDetail.requisites.vatNo')}
        </div>
      </div>
    </L.Subblock>
  );
});
